<template>
  <section class="schedule-week">
    <div v-if="scheduleTypes.length > 1" class="schedule-type">
      <button
        v-for="item in scheduleTypes"
        :key="`schedule-type-${item.type}`"
        type="button"
        class="change_class_icon"
        :class="{ hidden: Number(scheduleType) === Number(item.type) }"
        @click="$emit('change-schedule-type', item.type)"
      >
        <span class="material-icons">{{ item.icon }}</span>
      </button>
    </div>

    <div v-if="loading" class="schedule-week__loading" />

    <EmptyStateNotice v-else-if="isWeekEmpty" :text="t('scheduleEmpty')" variant="info" />

    <div v-else class="week__body">
      <article
        v-for="(day, index) in weekDays"
        :key="`week-day-${index}`"
        class="week-schedule"
      >
        <div class="resttime h3">
          <span>{{ formatDayTitle(day.ts) }}</span>
        </div>

        <div
          v-for="(lessonItem, lessonIndex) in day.lessons"
          :key="`lesson-${index}-${lessonIndex}`"
          class="lesson"
          @click="$emit('go-day', day.ts)"
        >
          <div class="item">
            <div class="left index">{{ lessonItem.key }}</div>
            <div class="center subject">
              <template
                v-for="(lessonName, nameIndex) in Array.isArray(lessonItem.value?.lesson)
                  ? lessonItem.value.lesson
                  : []"
                :key="`name-${index}-${lessonIndex}-${nameIndex}`"
              >
                <div class="week__subject">
                  <div class="week__subject-name">
                    <span
                      class="material-icons text-pink mr-1 f-16"
                      v-if="isTestLesson(lessonName)"
                    >
                      star
                    </span>
                    {{ resolveSubjectByLang(lessonName?.subject, lang) }}
                  </div>
                  <div
                    v-if="Array.isArray(lessonName?.mark) && lessonName.mark.length"
                    class="marks"
                  >
                    <span class="f-12 mr-1">{{ t("mark") }}:</span>
                    <span
                      v-for="(mark, markIndex) in lessonName.mark"
                      :key="`week-mark-${index}-${lessonIndex}-${nameIndex}-${markIndex}`"
                      class="bold mr-1 d-inline-flex align-items-center"
                      :class="getColorClassByMark(mark)"
                    >
                      <span v-if="markIndex > 0" class="mr-1">,</span>
                      <span
                        v-if="Number(mark?.m_id || 0) === 103"
                        class="material-icons text-pink f-16 mr-1"
                      >
                        star
                      </span>
                      {{ Number(mark?.ball) >= 0 ? mark.ball : mark?.mark_value }}
                    </span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div v-if="!day.lessons.length" class="holiday-text">
          {{ emptyDayText(day) }}
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import EmptyStateNotice from "@/components/common/EmptyStateNotice.vue";
import { getColorClassByMark, resolveSubjectByLang } from "@/utils/schedule";

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  weekItem: {
    type: Object,
    default: null,
  },
  weekDays: {
    type: Array,
    default: () => [],
  },
  lang: {
    type: String,
    default: "ru",
  },
  scheduleTypes: {
    type: Array,
    default: () => [],
  },
  scheduleType: {
    type: Number,
    default: 0,
  },
});

defineEmits(["change-schedule-type", "go-day"]);

const { t } = useI18n();
const locale = computed(() =>
  localStorage.getItem("translation") === "kz" ? "kk-KZ" : "ru-RU"
);

const isWeekEmpty = computed(
  () => !props.weekDays.length || props.weekDays.every((day) => !day.lessons.length)
);

function formatDayTitle(ts) {
  const date = new Date(Number(ts || 0));
  return date.toLocaleDateString(locale.value, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function isTestLesson(lesson) {
  const plans = Array.isArray(lesson?.lplans) ? lesson.lplans : [];
  return plans.some((item) => {
    const view = Number(item?.lesson_view || 0);
    return view === 21 || view === 22 || view === 33;
  });
}

function emptyDayText(day) {
  if (day?.isVacation) return t("holiday");
  if (day?.isWeekend) return t("dayOff");
  return t("holiday");
}
</script>

<style scoped>
.schedule-week {
  padding: 8px 0 12px;
}

.schedule-type {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 12px 8px;
}

.change_class_icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #66bb6a;
  color: #fff;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.change_class_icon.hidden {
  display: none;
}

@media screen and (max-width: 768px) {
  .schedule-type {
    position: fixed;
    width: 100%;
    justify-content: flex-end;
    bottom: 70px;
    z-index: 100;
    right: 0;
    padding-right: 14px;
  }
}

.week__body {
  padding: 0 12px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
}

.week-schedule {
  width: 100%;
  min-width: 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  padding: 12px 16px;
  box-shadow: none !important;
}

.resttime {
  position: relative;
  font-size: 14px;
  text-align: center;
  color: #4f4f4f;
  font-family: Roboto, sans-serif;
  font-weight: 600 !important;
  line-height: 1.2;
}

.resttime span {
  display: inline-block;
  position: relative;
  z-index: 2;
  padding: 0 8px;
  background-color: #fff;
  text-transform: capitalize;
  font-family: Roboto, sans-serif;
}

.resttime:before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  border-top: 1px dashed #c2cfd6;
}

.lesson {
  border-top: 1px solid #f2f2f2;
  cursor: pointer;
}

.lesson:first-of-type {
  border-top: none;
}

.item {
  display: flex;
  align-items: center;
  gap: 0;
  min-height: 28px;
  overflow: hidden;
  font-size: 14px;
  width: 100%;
}

.left.index {
  width: 24px;
  color: #68d286 !important;
  font-weight: 700;
  font-size: 16px;
  text-align: center;
}

.center.subject {
  flex: 1;
}

.week__subject {
  font-size: 14px;
  color: #29363d;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 28px;
}

.week__subject-name {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
  text-align: left;
}

.marks {
  font-size: 14px;
  color: #4f4f4f;
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  white-space: nowrap;
}

.marks .bold {
  font-weight: 600 !important;
  font-size: 15px !important;
}

.marks .f-12 {
  font-size: 11px !important;
}

.green {
  color: #0d8740 !important;
}

.yellow {
  color: #9bca16 !important;
}

.orange {
  color: #f2c94c !important;
}

.red {
  color: #eb5757 !important;
}

.grey {
  color: #bdbdbd !important;
}

.text-pink {
  color: #ff5d8a !important;
}

.week__subject .material-icons,
.marks .material-icons {
  font-size: 16px !important;
  line-height: 1;
}

.week__subject-name .material-icons {
  margin-right: 6px !important;
}

.holiday-text {
  text-align: center;
  font-size: 14px;
  color: #36b453;
  margin: 8px 0;
}

@media screen and (min-width: 768px) {
  .schedule-type {
    padding: 0 36px 8px;
  }

  .week__body {
    padding: 0 36px;
    gap: 12px;
  }
}

@media screen and (min-width: 950px) {
  .week-schedule {
    width: auto;
    flex: 1 1 calc((100% - 32px) / 3);
    min-width: 280px;
    max-width: 336px;
    height: 290px;
    overflow-y: auto !important;
    border-radius: 8px !important;
  }
}

@media screen and (min-width: 1320px) {
  .week__body {
    justify-content: flex-start;
    gap: 16px;
  }
}
</style>
